import { OrientationType } from "@package/ui-gql/index-data-access";
import { useEffect, useState, type RefObject } from "react";

export type ContainerSize = {
  width: number;
  height: number;
  orientation: OrientationType;
};

const defaultContainerSize: ContainerSize = {
  width: 0,
  height: 0,
  orientation: OrientationType.Portrait,
};

export function useContainerSize(
  ref: RefObject<HTMLElement | null>,
): ContainerSize {
  const [size, setSize] = useState<ContainerSize>(defaultContainerSize);

  useEffect(
    function effectOnContainerResized() {
      const element = ref.current;

      if (!element) {
        return;
      }

      const updateSize = (width: number, height: number) => {
        setSize({
          width,
          height,
          orientation:
            width > height
              ? OrientationType.Landscape
              : OrientationType.Portrait,
        });
      };

      updateSize(element.clientWidth, element.clientHeight);

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          const box = entry.contentBoxSize?.[0];
          if (box) {
            updateSize(box.inlineSize, box.blockSize);
          } else {
            updateSize(entry.contentRect.width, entry.contentRect.height);
          }
        }
      });

      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    },
    [ref],
  );

  return size;
}
